function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('collection-info')) {
  customElements.define(
    'collection-info',
    class CollectionInfo extends HTMLElement {
      constructor() {
        super();
        this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
        this.addEventListener('change', this.debounceOnChange.bind(this));
        this.addEventListener('click', this.onClickHandler.bind(this));
        
        this.page = 1;
        this.totalPages = 1;
        this.isLoading = false;
        this.hasMorePages = false;
        this.loadMoreObserver = null;
      }

      connectedCallback() {
        this.initFilterToggle();
        setTimeout(() => {
          this.initInfiniteScroll();
        }, 100);
      }

      disconnectedCallback() {
        this.cleanupInfiniteScroll();
      }

      initInfiniteScroll() {
        const paginationElement = this.querySelector('.pagination-wrapper');
        
        if (paginationElement) {
          const currentPageElement = this.querySelector('.pagination__item--current');
          const nextPageLink = this.querySelector('.pagination__item--next');
          
          if (currentPageElement) {
            this.page = parseInt(currentPageElement.textContent) || 1;
          }
          
          if (nextPageLink) {
            this.hasMorePages = true;
            this.totalPages = this.page + 1;
          } else {
            this.hasMorePages = false;
            this.totalPages = this.page;
          }
          
          if (this.hasMorePages) {
            this.setupInfiniteLoading();
            paginationElement.style.display = 'none';
          }
        }
      }

      setupInfiniteLoading() {
        const productGrid = this.querySelector('.product-grid');
        if (!productGrid) return;

        const loadingTrigger = document.createElement('div');
        loadingTrigger.className = 'loading-trigger';
        loadingTrigger.style.cssText = `
          text-align: center;
          padding: 20px;
          color: #666;
          font-size: 14px;
        `;
        loadingTrigger.textContent = 'Load more...';

        const productGridWrapper = productGrid.parentElement;
        productGridWrapper.appendChild(loadingTrigger);

        this.loadMoreObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
                this.loadMoreProducts();
              }
            });
          },
          {
            root: null,
            rootMargin: '0px 0px 400px 0px',
            threshold: 0.1
          }
        );

        this.loadMoreObserver.observe(loadingTrigger);
      }

      async loadMoreProducts() {
        if (this.isLoading || !this.hasMorePages) return;

        this.isLoading = true;

        try {
          const nextPage = this.page + 1;
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.set('page', nextPage);
          
          const url = `${window.location.pathname}?${currentParams.toString()}`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');
          
          const responseProductGrid = html.querySelector('.product-grid');
          const newProducts = responseProductGrid ? responseProductGrid.querySelectorAll('product-card') : [];
          const productGrid = this.querySelector('.product-grid');

          if (newProducts.length > 0 && productGrid) {
            newProducts.forEach(product => {
              productGrid.appendChild(product.cloneNode(true));
              
              if (window.Shopify && window.Shopify.designMode) {
                product.querySelectorAll('[data-cascade]').forEach(element => {
                  element.classList.add('animate--slide-in');
                });
              }
            });

            this.page = nextPage;
            
            const nextPageLinkInResponse = html.querySelector('.pagination__item--next');
            this.hasMorePages = !!nextPageLinkInResponse;

            if (!this.hasMorePages) {
              this.cleanupInfiniteScroll();
            }
          } else {
            this.hasMorePages = false;
            this.cleanupInfiniteScroll();
          }
        } catch (error) {
          this.hasMorePages = false;
          this.cleanupInfiniteScroll();
        } finally {
          this.isLoading = false;
        }
      }

      cleanupInfiniteScroll() {
        const loadingTrigger = this.querySelector('.loading-trigger');
        if (loadingTrigger) {
          loadingTrigger.remove();
        }
        
        if (this.loadMoreObserver) {
          this.loadMoreObserver.disconnect();
          this.loadMoreObserver = null;
        }
      }

      resetInfiniteScroll() {
        this.cleanupInfiniteScroll();
        this.page = 1;
        this.isLoading = false;
        this.hasMorePages = false;
      }

      initFilterToggle() {
        const hideFiltersButton = document.querySelector('.hide__filters_button_button');

        if (hideFiltersButton) {
          hideFiltersButton.addEventListener('click', this.toggleFilters.bind(this));
        }
      }

      toggleFilters(event) {
        event.preventDefault();
        const filtersSidebar = document.querySelector('.filters-sidebar');
        const hideFiltersButton = document.querySelector('.hide__filters_button_button');

        if (!filtersSidebar || !hideFiltersButton) {
          return;
        }

        const isHidden = filtersSidebar.classList.toggle('hidden');
        const buttonTextSpan = hideFiltersButton.querySelector('span');

        if (buttonTextSpan) {
          buttonTextSpan.textContent = isHidden ? 'Show Filters' : 'Hide Filters';
        }
      }

      onClickHandler = (event) => {
        if (event.target.matches('[data-render-section-url]')) {
          event.preventDefault();
          const searchParams = new URLSearchParams(event.target.dataset.renderSectionUrl.split('?')[1]).toString();
          this.fetchSection(searchParams);
        }
      };

      onChangeHandler = (event) => {
        if (!event.target.matches('[data-render-section]')) return;

        this.resetInfiniteScroll();

        const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
        const formData = new FormData(form);
        let searchParams = new URLSearchParams(formData).toString();

        if (window.location.search.includes('?q=')) {
          const existingParams = new URLSearchParams(window.location.search);
          const qValue = existingParams.get('q');

          searchParams = `q=${qValue}&${searchParams}`;
        }

        this.fetchSection(searchParams);
      };

      get form() {
        return this.querySelector('collection-info form');
      }

      updateSourceFromDestination = (html, id) => {
        const source = html.getElementById(`${id}`);
        const destination = this.querySelector(`#${id}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
      };

      updateURL(searchParams) {
        history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
      }

      updateUrlHistory(url) {
        const urlObj = new URL(url, window.location.origin);
        const searchParams = urlObj.searchParams.toString();

        this.fetchSection(searchParams);
      }

      updateFilters = (html, className) => {
        const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
        const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

        Array.from(filtersFromDom).forEach((currentElement) => {
          if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
            currentElement.remove();
          }
        });

        Array.from(filtersFromFetch).forEach((elementToRender, index) => {
          document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        });
      };

      showLoadingOverlay = () => {
        const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
        const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
        const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
        const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
        const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

        if (loadingOverlay) loadingOverlay.style.display = 'flex';
        if (loadingSpinner) loadingSpinner.style.display = 'block';
        if (resultsCount) resultsCount.style.display = 'none';
        if (drawerResultsCount) drawerResultsCount.style.display = 'none';
        if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'block';
      };

      hideLoadingOverlay = () => {
        const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
        const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
        const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
        const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
        const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

        if (loadingOverlay) loadingOverlay.style.display = 'none';
        if (loadingSpinner) loadingSpinner.style.display = 'none';
        if (resultsCount) resultsCount.style.display = 'block';
        if (drawerResultsCount) drawerResultsCount.style.display = 'block';
        if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'none';
      };

      fetchSection = (searchParams) => {
        this.showLoadingOverlay();
        fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
          .then((response) => response.text())
          .then((responseText) => {
            let html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateURL(searchParams);
            this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
            this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
            this.updateFilters(html, `js-filter`);
            this.hideLoadingOverlay();
            
            setTimeout(() => {
              this.initInfiniteScroll();
            }, 100);
            
            if(window.location.href.indexOf('page=') > -1){
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          })
          .catch((error) => {
            console.error(error);
            this.hideLoadingOverlay();
          });
      };
    }
  );
}